import { ref, computed } from 'vue';
import type { Ref } from 'vue';
import { defineStore } from 'pinia';
import { loadGA4Script, loadPendoScript } from '../analytics/loaders';
import { useServices } from '../api/services';

export interface TenantBrandingConfig {
  primaryColor: string;
  primaryHover: string;
  primaryLight: string;
  primaryText: string;
  secondaryColor: string;
  logoUrl: string;
  tenantPhone: string;
  tenantSlug: string;
  // 'stripe' is dead code as of 2026-05 — no tenants use it. Retained in the union for
  // legacy API payload compatibility only; do not build a Stripe payment form path.
  // Tenant configs returning 'stripe' surface as a Sentry data anomaly (PODZ-1655).
  gatewayType: 'stripe' | 'gpi' | 'stax';
  featureFlags: Record<string, boolean>;
  // IDEA-234 portal scheduling control (PODZ-1835). Sourced from the TOP-LEVEL
  // `allow_schedule` boolean on the tenant show JSON (NOT `feature_flags`), and
  // read through `isSchedulingAllowed` — never `isFeatureEnabled`. Default-ON:
  // `TenantService` coalesces an absent wire value to `true` (AC6).
  allowSchedule: boolean;
  // IDEA-234 appointment self-service controls (PODZ-1836). Same top-level
  // tenant booleans as `allowSchedule`, read through `isRescheduleAllowed` /
  // `isCancellationAllowed` (never `isFeatureEnabled`). Default-ON: absent wire
  // values are coalesced to `true` in `TenantService` (AC6). They gate the
  // appointment Manage popover's Reschedule / Cancel actions independently.
  allowReschedule: boolean;
  allowCancel: boolean;
  ga4TenantPropertyId: string | null;
  supportEmail: string;
  tenantName: string;
  timezone: string;
  faviconUrl: string;
  // Tenant-customized SMS opt-in copy returned by the v1 customizations
  // endpoint as `General.communication_disclaimer_non_marketing` and
  // `communication_disclaimer_marketing`. Both may contain HTML anchor
  // tags (terms / privacy links) — render with v-html. Defaults are
  // returned by the API when the tenant has not overridden the value.
  communicationDisclaimerNonMarketing: string;
  communicationDisclaimerMarketing: string;
  // Tenant-customized message shown when a service-address zip is outside
  // the tenant's coverage area. Returned by /v1/customizations as
  // General.out_of_coverage_message — API personalizes the default with
  // the tenant's name. Plain text (no HTML in the seed default).
  outOfCoverageMessage: string;
  // Invoice/quote footer customizations (Phase 5.2 D-17). Sourced from the
  // SAME `/v1/customizations` payload the tenant service already fetches at
  // init — no extra request (research Open Q1 → "extend tenant config" path).
  // All resolved `.value ?? .default`. Optional + defaulted to '' so existing
  // tenant-config callers/tests are non-regressive (the customizations payload
  // is the canonical source, mirroring the SMS-disclaimer treatment).
  // `licenseHtml`/`termsHtml` are admin-curated HTML strings — the footer card
  // renders them via
  // v-html (same precedent as the SMS disclaimers), so the raw markup is
  // preserved here verbatim.
  checkPayableTo?: string;
  licenseHtml?: string;
  termsHtml?: string;
}

/**
 * Canonical set of feature flag keys we recognise on `featureFlags`. The
 * underlying wire shape is `Record<string, boolean>` (legacy adapter spreads
 * `tenant.feature_flags` verbatim, so any backend rename could silently flip
 * a gate to undefined→false). This type literal is the seam for compile-time
 * typo detection at the call site — `isFeatureEnabled('financing')` is
 * type-checked; `isFeatureEnabled('Financing')` is a TS error.
 *
 * To add a new flag: extend this union. WR-04 (Phase 5.5 review fix).
 *
 * Phase 8 (D-14, PAYLINK-15): `pay_by_sms` / `pay_by_email` / `pay_by_qr` are the
 * three pay-by-link delivery-channel flags. All are default-OFF; PayView's
 * in-view visibility gate is enabled by ANY of them, so a tenant running a
 * single channel (e.g. email only) can still open the links it sends.
 * There is deliberately NO single combined link flag — the DR dropped that
 * assumed shape (D-14, 08-RESEARCH Pitfall 6).
 */
export type KnownFeatureFlag =
  | 'financing'
  | 'progress_billing'
  | 'pay_by_sms'
  | 'pay_by_email'
  | 'pay_by_qr';

/** Footer contact/license/terms surface shape (Phase 5.2 D-17). */
export interface FooterContact {
  phone: string;
  supportEmail: string;
  checkPayableTo: string;
  /** Tenant-authored HTML — sanitize with DOMPurify before any v-html bind;
   *  never render this untrusted markup raw (D-17 / T-5.2-05). */
  licenseHtml: string;
  /** Tenant-authored HTML — sanitize with DOMPurify before any v-html bind;
   *  never render this untrusted markup raw (D-17 / T-5.2-05). */
  termsHtml: string;
}

/**
 * Resolve the 5 footer fields off the tenant config (already lifted from
 * `/v1/customizations` at init). Extracted to a module-level helper so the
 * store-setup function and the getter both stay under the lint
 * line-count/complexity ceilings (mirrors the Phase 5.2 extract-to-helper
 * pattern). Empty strings when config or a field is unset.
 */
function resolveFooterContact(
  config: TenantBrandingConfig | null,
): FooterContact {
  // Coerce each field through a single nullish helper so the function stays
  // under the cyclomatic-complexity ceiling (five inline `??` chains tripped
  // it at 11) while keeping the empty-string-when-unset contract.
  const str = (v: string | undefined): string => v ?? '';
  return {
    phone: str(config?.tenantPhone),
    supportEmail: str(config?.supportEmail),
    checkPayableTo: str(config?.checkPayableTo),
    licenseHtml: str(config?.licenseHtml),
    termsHtml: str(config?.termsHtml),
  };
}

/**
 * Whether the tenant allows customers to self-schedule (IDEA-234 / PODZ-1835).
 *
 * Reads the TOP-LEVEL `allowSchedule` field — deliberately NOT routed through
 * `isFeatureEnabled` (that returns `=== true`, so a missing key would read as
 * false and DISABLE scheduling for every unconfigured tenant, violating AC6).
 * The gate is default-ON: only an explicit `false` turns scheduling off.
 *   - config null (pre-init)         → true
 *   - allowSchedule true / undefined → true
 *   - allowSchedule false            → false
 *
 * Extracted to a module-level helper (mirrors `resolveFooterContact`) so the
 * store-setup function stays under the max-lines-per-function ceiling.
 */
function canSchedule(config: TenantBrandingConfig | null): boolean {
  return config?.allowSchedule !== false;
}

/**
 * Whether the tenant allows customers to self-reschedule / self-cancel an
 * appointment (IDEA-234 / PODZ-1836). Same default-ON contract as
 * `canSchedule`: only an explicit `false` turns the action off; a null config
 * (pre-init), an absent key, or `undefined`/`true` all read as allowed. Kept as
 * two module-level helpers (mirroring `canSchedule`) so the store-setup
 * function stays under the max-lines-per-function ceiling.
 */
function canReschedule(config: TenantBrandingConfig | null): boolean {
  return config?.allowReschedule !== false;
}

function canCancel(config: TenantBrandingConfig | null): boolean {
  return config?.allowCancel !== false;
}

/**
 * Push a tenant's brand colors + favicon onto the document. Extracted to a
 * module-level helper (mirrors `canSchedule` / `resolveFooterContact`) so the
 * store-setup function stays under the max-lines-per-function ceiling — the DOM
 * mutation needs no store closure, only the incoming config.
 */
/**
 * Wrap a boot function so repeated calls share ONE in-flight/resolved promise —
 * idempotent init (PODZ-1985). App.vue's onBeforeMount and the router's
 * makeSchedulingGuard both call init() on a cold load; the memo makes them share
 * a single boot (no duplicate tenant fetch / GA4+Pendo double-load). On rejection
 * the memo cell is cleared so a later call (App.vue's Reload) can retry, and the
 * error is re-thrown so callers still see it. The memo lives on the passed
 * store-scoped ref, so it resets per Pinia instance and never leaks across tests
 * or tenant/subdomain switches. Extracted to module level (mirrors `canSchedule`
 * / `resolveFooterContact`) so the store-setup function stays under the
 * max-lines-per-function ceiling.
 */
function memoizedBoot(
  memo: Ref<Promise<void> | null>,
  run: () => Promise<void>,
): () => Promise<void> {
  return () => {
    if (memo.value) return memo.value;
    memo.value = run().catch((err) => {
      memo.value = null;
      throw err;
    });
    return memo.value;
  };
}

/**
 * Resolve the tenant subdomain the config/customizations endpoints are keyed
 * by. On localhost we fall back to `VITE_LOCAL_TENANT` (CLAUDE.md multi-tenancy
 * convention); in every deployed env it is the first hostname label. Extracted
 * to module level so both the boot (`runInit`) and the mid-session `refresh`
 * derive it identically.
 */
function resolveSubdomain(): string {
  return import.meta.env.DEV
    ? import.meta.env.VITE_LOCAL_TENANT
    : window.location.hostname.split('.')[0];
}

function applyBrandingVars(brandConfig: TenantBrandingConfig): void {
  const root = document.documentElement;
  root.style.setProperty('--color-primary-base', brandConfig.primaryColor);
  root.style.removeProperty('--color-primary');
  root.style.removeProperty('--color-primary-hover');
  root.style.removeProperty('--color-primary-light');
  root.style.setProperty('--color-secondary', brandConfig.secondaryColor);
  if (brandConfig.faviconUrl) {
    const link = document.querySelector<HTMLLinkElement>('link[rel="icon"]');
    if (link) link.href = brandConfig.faviconUrl;
  }
}

export const useTenantStore = defineStore('tenant', () => {
  const config = ref<TenantBrandingConfig | null>(null);
  const appReady = computed(() => config.value !== null);

  // PODZ-1985: store-scoped memo cell for the idempotent boot promise (see
  // `memoizedBoot`). Declared inside the setup fn so it resets per Pinia instance.
  const initPromise = ref<Promise<void> | null>(null);

  function applyBranding(brandConfig: TenantBrandingConfig): void {
    applyBrandingVars(brandConfig);
    config.value = brandConfig;
  }

  async function runInit(): Promise<void> {
    const { tenant } = useServices();
    const brandConfig = await tenant.init(resolveSubdomain());
    applyBranding(brandConfig);

    const platformGa4 = import.meta.env.VITE_GA4_PLATFORM_ID;
    if (platformGa4) loadGA4Script(platformGa4);
    if (
      brandConfig.ga4TenantPropertyId &&
      brandConfig.ga4TenantPropertyId !== platformGa4
    )
      loadGA4Script(brandConfig.ga4TenantPropertyId);
    loadPendoScript(import.meta.env.VITE_PENDO_API_KEY);
  }

  const init = memoizedBoot(initPromise, runInit);

  /**
   * Re-fetch the tenant config from the API and update the reactive `config`
   * in place. Unlike `init()` this is deliberately NOT memoized — it always
   * hits the network so a scheduling control (`allow_schedule` /
   * `allow_reschedule` / `allow_cancel`) flipped server-side AFTER boot is seen
   * on the click that triggers it. The scheduling route guard and the
   * appointment Manage popover call this immediately before reading the gate
   * accessors so a stale-enabled action is blocked once the fresh value lands.
   *
   * Re-applies branding (idempotent CSS-var write) but does NOT re-run the
   * one-time GA4/Pendo script loaders — those are boot-only side effects and
   * re-loading them would double-register analytics. Rejects on network error;
   * callers fail-open (keep the last-known config) to preserve the default-ON
   * contract on a transient blip.
   */
  async function refresh(): Promise<void> {
    const { tenant } = useServices();
    const brandConfig = await tenant.init(resolveSubdomain());
    applyBranding(brandConfig);
  }

  /**
   * Named-flag accessor with compile-time typo detection (WR-04).
   *
   * Call sites read feature flags through this helper instead of dotting
   * into `config?.featureFlags?.[name]` directly. The TS literal type on
   * `flag` rejects unknown keys at build time so a typo
   * (`isFeatureEnabled('financingg')`) becomes a type error rather than a
   * silently-always-false runtime gate.
   *
   * Returns false when:
   *   - config is null (pre-init), OR
   *   - featureFlags is absent on config, OR
   *   - the key is absent from featureFlags, OR
   *   - the key is present but not exactly `=== true`
   */
  function isFeatureEnabled(flag: KnownFeatureFlag): boolean {
    return config.value?.featureFlags?.[flag] === true;
  }

  const isSchedulingAllowed = computed(() => canSchedule(config.value));
  const isRescheduleAllowed = computed(() => canReschedule(config.value));
  const isCancellationAllowed = computed(() => canCancel(config.value));

  /**
   * Footer contact / license / terms surface for the invoice (and quote)
   * footer card (Phase 5.2 D-17). Reads the 5 fields the tenant service
   * already lifted off `/v1/customizations` at init — no extra fetch. Empty
   * strings when unset so the footer card can `v-if` per-field. `licenseHtml`
   * / `termsHtml` are admin-curated HTML strings rendered via v-html (same
   * precedent as the SMS-consent disclaimers in WizardStepConfirm).
   */
  const footerContact = computed<FooterContact>(() =>
    resolveFooterContact(config.value),
  );

  return {
    config,
    appReady,
    footerContact,
    applyBranding,
    init,
    refresh,
    isFeatureEnabled,
    isSchedulingAllowed,
    isRescheduleAllowed,
    isCancellationAllowed,
  };
});
